import { FC } from 'react'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import StarRoundedIcon from '@mui/icons-material/StarRounded'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import FavoriteIcon from '@mui/icons-material/Favorite'
import PlaylistAddRoundedIcon from '@mui/icons-material/PlaylistAddRounded'
import PlaylistAddCheckRoundedIcon from '@mui/icons-material/PlaylistAddCheckRounded'
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded'
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded'
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled'
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded'
import { styled } from '@mui/material/styles'
import { Tooltip } from '@mui/material'

import {
  StyledBannerContent,
  StyledPoster,
  StyledPosterFallback,
  StyledBannerInfo,
  StyledTitle,
  StyledTagline,
  StyledMetaWrapper,
  StyledRating,
  StyledMetaText,
  StyledCertification,
  StyledGenreWrapper,
  StyledGenreChip,
  StyledButtonWrapper,
  StyledPrimaryButton,
  StyledIconButton,
  MobileButtonsContainer,
  MobileButtonRow,
  StyledUserRatingRow,
  StyledUserRatingLabel,
  StyledClearRatingButton,
  StyledMetaHint,
  StyledUserRatingStars,
  StyledUserRatingRowSection,
} from './details-page-styles'
import LazyImage from '../lazy-image'
import { useMobileBreakpoints } from '../../utils/mobile-breakpoints'

// Active-state icons: colored once the title is favorited / watched / watchlisted
const FavoritedIcon = styled(FavoriteIcon)(({ theme }) => ({
  color: theme.palette.brand.main,
}))

const WatchedIcon = styled(AccessTimeFilledIcon)(({ theme }) => ({
  color: theme.palette.green.main,
}))

const RemindMeActiveIcon = styled(NotificationsActiveRoundedIcon)({
  color: '#fbc02d',
})

const WatchlistedIcon = styled(PlaylistAddCheckRoundedIcon)({
  color: '#fbc02d',
})

type DetailsHeaderProps = {
  title: string
  tagline?: string
  posterUrl?: string
  rating: number
  year?: string
  runtimeOrStatus?: string
  certification?: string
  genres: string[]
  hasTrailer: boolean
  onWatchTrailer?: () => void
  showImdbButton?: boolean
  onOpenImdb?: () => void
  isFavorite?: boolean
  isWatchlisted?: boolean
  isHistory?: boolean
  onToggleFavorite?: () => void
  onToggleWatchlist?: () => void
  onToggleHistory?: () => void
  disableWatchlist?: boolean
  disableFavorite?: boolean
  disableHistory?: boolean
  actionsEnabled?: boolean
  // Personal 1-10 rating - separate from the TMDB score above. Row only renders
  // when showUserRating is true (logged-in users on movie/show pages).
  showUserRating?: boolean
  userRating?: number | null
  onUserRatingChange?: (value: number | null) => void
  // Ratings unlock once the title is in the user's watch history.
  userRatingLocked?: boolean
  // Unreleased titles can't be watched/rated/commented; the watchlist button
  // becomes "Remind Me" (release-day email via the watchlist scan).
  unreleased?: boolean
  releaseDateDisplay?: string
  // Separate new-episode reminder bell, shown on released shows that are still
  // returning (hidden for ended/canceled). Independent from the watchlist.
  showRemindButton?: boolean
  isReminding?: boolean
  onToggleRemind?: () => void
  disableRemind?: boolean
}

const DetailsHeader: FC<DetailsHeaderProps> = ({
  title,
  tagline,
  posterUrl,
  rating,
  year,
  runtimeOrStatus,
  certification,
  genres,
  hasTrailer,
  onWatchTrailer,
  showImdbButton,
  onOpenImdb,
  isFavorite = false,
  isWatchlisted = false,
  isHistory = false,
  onToggleFavorite,
  onToggleWatchlist,
  onToggleHistory,
  disableWatchlist = false,
  disableFavorite = false,
  disableHistory = false,
  actionsEnabled = true,
  showUserRating = false,
  userRating = null,
  onUserRatingChange,
  userRatingLocked = false,
  unreleased = false,
  releaseDateDisplay,
  showRemindButton = false,
  isReminding = false,
  onToggleRemind,
  disableRemind = false,
}) => {
  const { isMobileMD } = useMobileBreakpoints()

  const watchlistLabel = unreleased
    ? isWatchlisted
      ? "We'll remind you"
      : 'Remind Me'
    : isWatchlisted
      ? 'In Watchlist'
      : 'Add to Watchlist'
  const watchlistIcon = unreleased ? (
    isWatchlisted ? (
      <RemindMeActiveIcon />
    ) : (
      <NotificationsNoneRoundedIcon />
    )
  ) : isWatchlisted ? (
    <WatchlistedIcon />
  ) : (
    <PlaylistAddRoundedIcon />
  )

  return (
    <StyledBannerContent>
      <StyledPoster>
        <LazyImage
          src={posterUrl}
          alt={title}
          fallback={<StyledPosterFallback>No poster</StyledPosterFallback>}
        />
      </StyledPoster>

      <StyledBannerInfo>
        <StyledTitle>{title}</StyledTitle>

        {tagline && <StyledTagline>"{tagline}"</StyledTagline>}

        <StyledMetaWrapper>
          {rating ? (
            <StyledRating value={rating}>
              <StarRoundedIcon fontSize='inherit' />
              {rating}
            </StyledRating>
          ) : null}
          {year && <StyledMetaText>{year}</StyledMetaText>}
          {runtimeOrStatus && (
            <StyledMetaText>{runtimeOrStatus}</StyledMetaText>
          )}
          {certification && (
            <StyledCertification>{certification}</StyledCertification>
          )}
        </StyledMetaWrapper>

        {unreleased && (
          <StyledMetaHint>
            {releaseDateDisplay
              ? `Releases ${releaseDateDisplay}. `
              : 'Not released yet. '}
            Watching, rating, and commenting unlock after release.
          </StyledMetaHint>
        )}

        {showUserRating && (
          <StyledUserRatingRowSection>
            <StyledUserRatingLabel>Your Rating</StyledUserRatingLabel>
            <StyledUserRatingRow>
              <StyledUserRatingStars
                max={10}
                precision={0.5}
                value={userRating ?? 0}
                disabled={userRatingLocked}
                onChange={(_, value) => onUserRatingChange?.(value)}
                emptyIcon={<StarBorderRoundedIcon fontSize='inherit' />}
              />
              {userRatingLocked ? (
                <StyledMetaHint>
                  {unreleased
                    ? 'You can rate it after release'
                    : 'Mark it as watched to rate'}
                </StyledMetaHint>
              ) : null}
              {userRating ? (
                <>
                  {/* Same star-and-number style as the TMDB score */}
                  <StyledRating value={userRating}>
                    <StarRoundedIcon fontSize='inherit' />
                    {userRating}
                  </StyledRating>
                  <Tooltip title='Clear rating'>
                    <StyledClearRatingButton
                      size='small'
                      onClick={() => onUserRatingChange?.(null)}
                    >
                      <CloseRoundedIcon />
                    </StyledClearRatingButton>
                  </Tooltip>
                </>
              ) : null}
            </StyledUserRatingRow>
          </StyledUserRatingRowSection>
        )}

        <StyledGenreWrapper>
          {genres.map((genre) => (
            <StyledGenreChip key={genre}>{genre}</StyledGenreChip>
          ))}
        </StyledGenreWrapper>

        <StyledButtonWrapper>
          {/* Desktop layout */}
          {!isMobileMD && (
            <>
              {hasTrailer && onWatchTrailer && (
                <StyledPrimaryButton
                  startIcon={<PlayArrowIcon />}
                  onClick={onWatchTrailer}
                >
                  Watch Trailer
                </StyledPrimaryButton>
              )}

              {actionsEnabled && (
                <>
                  <StyledPrimaryButton
                    startIcon={
                      isFavorite ? <FavoritedIcon /> : <FavoriteBorderIcon />
                    }
                    onClick={onToggleFavorite}
                    disabled={disableFavorite}
                  >
                    {isFavorite ? 'Favorited' : 'Favorite'}
                  </StyledPrimaryButton>

                  <StyledPrimaryButton
                    startIcon={
                      isHistory ? <WatchedIcon /> : <AccessTimeOutlinedIcon />
                    }
                    onClick={onToggleHistory}
                    disabled={disableHistory || unreleased}
                  >
                    {isHistory ? 'Watched' : 'Mark as Watched'}
                  </StyledPrimaryButton>

                  <StyledPrimaryButton
                    startIcon={watchlistIcon}
                    onClick={onToggleWatchlist}
                    disabled={disableWatchlist}
                  >
                    {watchlistLabel}
                  </StyledPrimaryButton>

                  {showRemindButton && onToggleRemind && (
                    <StyledPrimaryButton
                      startIcon={
                        isReminding ? (
                          <RemindMeActiveIcon />
                        ) : (
                          <NotificationsNoneRoundedIcon />
                        )
                      }
                      onClick={onToggleRemind}
                      disabled={disableRemind}
                    >
                      {isReminding ? 'Reminders On' : 'Remind Me'}
                    </StyledPrimaryButton>
                  )}
                </>
              )}

              {showImdbButton && onOpenImdb && (
                <StyledPrimaryButton onClick={onOpenImdb}>
                  IMDB
                </StyledPrimaryButton>
              )}
            </>
          )}

          {/* Mobile layout */}
          {isMobileMD && (
            <MobileButtonsContainer>
              {/* Row 1: Action buttons (Favorite, Watched, Watchlist) */}
              {actionsEnabled && (
                <MobileButtonRow>
                  <Tooltip title={isFavorite ? 'Favorited' : 'Favorite'}>
                    <StyledIconButton
                      onClick={onToggleFavorite}
                      disabled={disableFavorite}
                    >
                      {isFavorite ? <FavoritedIcon /> : <FavoriteBorderIcon />}
                    </StyledIconButton>
                  </Tooltip>

                  <Tooltip
                    title={
                      unreleased
                        ? 'Available after release'
                        : isHistory
                          ? 'Watched'
                          : 'Mark as Watched'
                    }
                  >
                    <span>
                      <StyledIconButton
                        onClick={onToggleHistory}
                        disabled={disableHistory || unreleased}
                      >
                        {isHistory ? (
                          <WatchedIcon />
                        ) : (
                          <AccessTimeOutlinedIcon />
                        )}
                      </StyledIconButton>
                    </span>
                  </Tooltip>

                  <Tooltip title={watchlistLabel}>
                    <StyledIconButton
                      onClick={onToggleWatchlist}
                      disabled={disableWatchlist}
                    >
                      {watchlistIcon}
                    </StyledIconButton>
                  </Tooltip>

                  {showRemindButton && onToggleRemind && (
                    <Tooltip
                      title={
                        isReminding
                          ? 'New episode reminders on'
                          : 'Remind me about new episodes'
                      }
                    >
                      <StyledIconButton
                        onClick={onToggleRemind}
                        disabled={disableRemind}
                      >
                        {isReminding ? (
                          <RemindMeActiveIcon />
                        ) : (
                          <NotificationsNoneRoundedIcon />
                        )}
                      </StyledIconButton>
                    </Tooltip>
                  )}
                </MobileButtonRow>
              )}

              {/* Row 2: Trailer + IMDB (if IMDB exists) or just Trailer in row 1 */}
              {(hasTrailer || showImdbButton) && (
                <MobileButtonRow>
                  {hasTrailer && onWatchTrailer && (
                    <Tooltip title='Watch Trailer'>
                      <StyledIconButton onClick={onWatchTrailer}>
                        <PlayArrowIcon />
                      </StyledIconButton>
                    </Tooltip>
                  )}

                  {showImdbButton && onOpenImdb && (
                    <Tooltip title='IMDB'>
                      <StyledIconButton onClick={onOpenImdb}>
                        <OpenInNewIcon />
                      </StyledIconButton>
                    </Tooltip>
                  )}
                </MobileButtonRow>
              )}
            </MobileButtonsContainer>
          )}
        </StyledButtonWrapper>
      </StyledBannerInfo>
    </StyledBannerContent>
  )
}

export default DetailsHeader
